/**
 * AP Match Review tab (ReconciliationHub) — human-in-the-loop gate over
 * ap-bank-matcher. "Run Matcher" enqueues a propose run (drafts land in
 * ap_match_proposals); each draft pairing (payment ↔ bill/s, with evidence)
 * is approved (= settle) or rejected (terminal — never re-proposed) by a
 * human. Nothing auto-applies. Data: /api/accounting/ap-match-review.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Collapse, IconButton,
  Button, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Tooltip, Card, CardContent, Grid, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  runMatcher, getRunStatus, getStats, getPending,
  approveProposal, rejectProposal, bulkApprove,
} from '../../../services/apMatchReviewService';

const money = (v, ccy) => `${Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} ${ccy || ''}`;
const KIND_LABEL = { qb_payment: 'QB payment', bank_wise: 'Wise', bank_revolut: 'Revolut', bank_amex: 'Amex', bank_paypal: 'PayPal' };

function StatCard({ label, value, sub }) {
  return (
    <Card variant="outlined" sx={{ minWidth: 150 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6">{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

function ProposalRow({ p, selected, onSelect, onApprove, onReject, busy, rivalCount }) {
  const [open, setOpen] = useState(false);
  const multi = p.mode === 'multi';
  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <Checkbox size="small" checked={selected} onChange={e => onSelect(p.proposal_id, e.target.checked)} />
        </TableCell>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen(!open)} aria-label={open ? 'Collapse evidence' : 'Expand evidence'}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>{p.supplier_name}</Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip size="small" label={KIND_LABEL[p.payment_kind] || p.payment_kind} color={p.payment_kind === 'qb_payment' ? 'info' : 'primary'} sx={{ height: 18, fontSize: 11 }} />
            <Typography variant="body2">{money(p.payment_amount, p.payment_currency)}</Typography>
            <Typography variant="caption" color="text.secondary">on {String(p.payment_date).slice(0, 10)}</Typography>
            {p.qb_twin_ref && (
              <Tooltip title={`QB record ${p.qb_twin_ref} merged into this bank payment (same physical outflow)`}>
                <Chip size="small" label="QB twin" variant="outlined" sx={{ height: 18, fontSize: 11 }} />
              </Tooltip>
            )}
            {rivalCount > 1 && (
              <Tooltip title={`${rivalCount - 1} other draft(s) cite this SAME payment for a different bill — approve at most ONE of them (the rest will be blocked)`}>
                <Chip size="small" color="error" variant="outlined" label={`1 of ${rivalCount} rivals`} sx={{ height: 18, fontSize: 11 }} />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {(p.bill_invoice_numbers || []).join(', ')}
            {multi && <Chip size="small" label={`${(p.bill_ids || []).length} bills`} color="secondary" sx={{ ml: 0.5, height: 18, fontSize: 11 }} />}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Tooltip title={`difference ${money(p.amount_diff, p.payment_currency)} vs tolerance ${money(p.tolerance_used, p.payment_currency)}`}>
            <Typography variant="body2" color={Number(p.amount_diff) === 0 ? 'success.main' : 'text.primary'}>
              {Number(p.amount_diff) === 0 ? 'exact' : money(p.amount_diff, p.payment_currency)}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          {p.bucket === 'high_confidence'
            ? <Chip size="small" color="success" label="high confidence" />
            : <Chip size="small" color="warning" label="ambiguous" />}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          <Tooltip title="Approve: settle the bill(s) against this payment">
            <span><IconButton size="small" color="success" disabled={busy} onClick={() => onApprove(p)} aria-label="Approve"><CheckCircleIcon /></IconButton></span>
          </Tooltip>
          <Tooltip title="Reject: this pairing is wrong (never proposed again)">
            <span><IconButton size="small" color="error" disabled={busy} onClick={() => onReject(p)} aria-label="Reject"><CancelIcon /></IconButton></span>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1, ml: 6 }}>
              <Typography variant="caption" display="block"><b>Payment:</b> {p.payment_description || p.payment_ref}</Typography>
              <Typography variant="caption" display="block" color="text.secondary">ref {p.payment_ref}{p.qb_twin_ref ? ` · QB twin ${p.qb_twin_ref}` : ''} · run {p.run_id}</Typography>
              <Box sx={{ mt: 0.5 }}>
                {(p.bill_invoice_numbers || []).map((no, i) => (
                  <Typography key={no + i} variant="caption" display="block">
                    <b>{no}</b> — owed {money((p.bill_amounts_owed || [])[i], p.payment_currency)} (bill id {(p.bill_ids || [])[i]})
                  </Typography>
                ))}
              </Box>
              {p.ambiguity_note && <Alert severity="warning" sx={{ mt: 1, py: 0 }}>{p.ambiguity_note}</Alert>}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function ApMatchReview() {
  const [proposals, setProposals] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [bucket, setBucket] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirm, setConfirm] = useState(null);   // {kind:'approve'|'bulk', proposal?, ids?}
  const [rejecting, setRejecting] = useState(null); // proposal
  const [rejectReason, setRejectReason] = useState('');
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pend, st] = await Promise.all([
        getPending({ bucket: bucket === 'all' ? undefined : bucket, limit: 200 }),
        getStats(),
      ]);
      setProposals(pend.data?.proposals || []);
      setPendingTotal(Number(pend.data?.pagination?.total || 0));
      setStats(st.data?.by_status || []);
      setSelected(new Set());
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [bucket]);
  useEffect(() => { refresh(); }, [refresh]);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  const pollRun = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await getRunStatus();
        const job = res.data;
        if (!job || ['completed', 'failed', 'cancelled'].includes(job.status)) {
          stopPolling();
          setRunning(false);
          if (job?.status === 'failed') setError('Matcher run failed — check sync_jobs_v2 / worker logs.');
          else setNotice('Matcher run finished — drafts refreshed.');
          refresh();
        }
      } catch { /* transient poll errors: keep polling */ }
    }, 5000);
  }, [refresh]);
  useEffect(() => () => stopPolling(), []);
  // Resume polling if a run is already in flight when the tab opens.
  useEffect(() => {
    (async () => {
      try {
        const res = await getRunStatus();
        if (res.data && res.data.status === 'running') { setRunning(true); pollRun(); }
      } catch { /* non-fatal */ }
    })();
  }, [pollRun]);

  const handleRun = async () => {
    setError(null);
    setNotice(null);
    try {
      await runMatcher();
      setRunning(true);
      setNotice('Matcher run queued — drafts will refresh when it finishes.');
      pollRun();
    } catch (e) {
      if (e.response?.status === 409) { setRunning(true); pollRun(); setNotice(e.response.data?.error); }
      else setError(e.response?.data?.error || e.message);
    }
  };

  const doApprove = async (proposal, ids) => {
    setBusy(true);
    setError(null);
    try {
      if (proposal) {
        await approveProposal(proposal.proposal_id);
        setNotice(`Approved — ${proposal.supplier_name} ${(proposal.bill_invoice_numbers || []).join(', ')} settled.`);
      } else {
        const res = await bulkApprove(ids);
        const s = res.data?.summary || {};
        setNotice(`Bulk approve: ${s.approved} settled, ${s.failed} failed.`);
        if ((res.data?.details?.failed || []).length > 0) {
          setError(res.data.details.failed.map(f => {
            const src = proposals.find(o => o.proposal_id === f.proposal_id);
            const label = src ? `${src.supplier_name} (${(src.bill_invoice_numbers || []).join(', ')})` : f.proposal_id;
            return `${label}: ${f.error}`;
          }).join(' · '));
        }
      }
      await refresh();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await rejectProposal(rejecting.proposal_id, rejectReason.trim());
      setNotice(`Rejected — this pairing will never be proposed again.`);
      setRejecting(null);
      setRejectReason('');
      await refresh();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onSelect = (id, checked) => {
    const next = new Set(selected);
    if (checked) next.add(id); else next.delete(id);
    setSelected(next);
  };
  const selectAllHighConfidence = () => {
    setSelected(new Set(proposals.filter(p => p.bucket === 'high_confidence').map(p => p.proposal_id)));
  };

  const draftStats = stats.filter(s => s.status === 'draft');
  const nHigh = draftStats.filter(s => s.bucket === 'high_confidence').reduce((a, s) => a + Number(s.count), 0);
  const nAmb = draftStats.filter(s => s.bucket === 'ambiguous').reduce((a, s) => a + Number(s.count), 0);
  const nApplied = stats.filter(s => s.status === 'applied').reduce((a, s) => a + Number(s.count), 0);
  const nRejected = stats.filter(s => s.status === 'rejected').reduce((a, s) => a + Number(s.count), 0);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>AP Match Review</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The bank matcher proposes which real money movement paid each open supplier bill.
        Every match below is a DRAFT — nothing settles until you approve it. Approving writes the
        payment evidence onto the bill and marks it paid; rejecting permanently dismisses the pairing.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <StatCard label="High-confidence drafts" value={nHigh} sub="unique match, safe to bulk-approve after a scan" />
        <StatCard label="Ambiguous drafts" value={nAmb} sub="competing candidates — pick the right one" />
        <StatCard label="Applied (settled)" value={nApplied} />
        <StatCard label="Rejected" value={nRejected} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />} disabled={running} onClick={handleRun}>
          {running ? 'Matcher running…' : 'Run Matcher'}
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={refresh} disabled={loading}>Refresh</Button>
        <ToggleButtonGroup size="small" value={bucket} exclusive onChange={(e, v) => v && setBucket(v)}>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="high_confidence">High confidence</ToggleButton>
          <ToggleButton value="ambiguous">Ambiguous</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={selectAllHighConfidence} disabled={proposals.every(p => p.bucket !== 'high_confidence')}>
          Select all high-confidence
        </Button>
        <Button variant="outlined" color="success" disabled={selected.size === 0 || busy}
          onClick={() => setConfirm({ kind: 'bulk', ids: [...selected] })}>
          Approve selected ({selected.size})
        </Button>
      </Box>

      {notice && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {!loading && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell padding="checkbox" />
                <TableCell>Supplier</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Bill(s)</TableCell>
                <TableCell align="right">Diff</TableCell>
                <TableCell>Confidence</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proposals.map(p => (
                <ProposalRow key={p.proposal_id} p={p} selected={selected.has(p.proposal_id)}
                  onSelect={onSelect} busy={busy}
                  rivalCount={proposals.filter(o => o.payment_ref === p.payment_ref).length}
                  onApprove={pp => setConfirm({ kind: 'approve', proposal: pp })}
                  onReject={pp => { setError(null); setRejecting(pp); setRejectReason(''); }} />
              ))}
              {proposals.length === 0 && (
                <TableRow><TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {error
                      ? 'Could not load drafts — fix the error above and press Refresh.'
                      : 'No draft proposals. Press Run Matcher to scan open bills against bank and QB payments.'}
                  </Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!loading && pendingTotal > proposals.length && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Showing {proposals.length} of {pendingTotal} drafts — narrow with the bucket filter to see the rest.
        </Typography>
      )}

      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)}>
        <DialogTitle>Settle {confirm?.kind === 'bulk' ? `${confirm.ids.length} proposals` : 'this bill'}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirm?.kind === 'bulk'
              ? `The ${confirm.ids.length} selected proposals will each mark their bill(s) as PAID with the payment evidence written into the bill notes.`
              : confirm && `${confirm.proposal.supplier_name} — bill(s) ${(confirm.proposal.bill_invoice_numbers || []).join(', ')} will be marked PAID against ${KIND_LABEL[confirm.proposal.payment_kind] || confirm.proposal.payment_kind} ${money(confirm.proposal.payment_amount, confirm.proposal.payment_currency)} of ${String(confirm.proposal.payment_date).slice(0, 10)}.`}
          </Typography>
          {confirm?.kind === 'bulk' && (() => {
            const sel = proposals.filter(o => confirm.ids.includes(o.proposal_id));
            const refCounts = {};
            sel.forEach(o => { refCounts[o.payment_ref] = (refCounts[o.payment_ref] || 0) + 1; });
            return Object.values(refCounts).some(n => n > 1);
          })() && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Your selection includes rival drafts citing the SAME payment for different bills —
              only one per payment will settle; the rest will be blocked and dropped from the queue.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="success" disabled={busy}
            onClick={() => (confirm.kind === 'bulk' ? doApprove(null, confirm.ids) : doApprove(confirm.proposal))}>
            {busy ? 'Settling…' : 'Approve & settle'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!rejecting} onClose={() => !busy && setRejecting(null)}>
        <DialogTitle>Reject this match?</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Typography variant="body2" sx={{ mb: 1 }}>
            {rejecting && `${rejecting.supplier_name} — ${(rejecting.bill_invoice_numbers || []).join(', ')} ↔ ${money(rejecting.payment_amount, rejecting.payment_currency)}. Rejected pairings are never proposed again.`}
          </Typography>
          <TextField autoFocus fullWidth required label="Reason" value={rejectReason}
            onChange={e => setRejectReason(e.target.value)} size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="error" disabled={busy || !rejectReason.trim()} onClick={doReject}>
            {busy ? 'Rejecting…' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
